import * as React from 'react';
import { createResident } from '@/api/residents';
import { getAllUnits } from '@/api/units';
import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'sonner';

import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import ProfessionalDialog, { DialogActionButtons } from '@/components/common/professional-dialog';
import { UserPlus as UserPlusIcon } from '@phosphor-icons/react';

interface CreateResidentDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  communityId: string | number;
}

interface ResidentFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  unit_id: string;
  community_role?: 'community_manager' | 'community_member' | 'board_member';
  unit_role?: 'unit_owner' | 'unit_tenant' | 'unit_resident';
  send_invite?: boolean;
}

const ROLES_WITHOUT_UNIT_REQUIREMENT = ['community_manager', 'board_member'];

export default function CreateResidentDialog({ open, onClose, onCreated, communityId }: CreateResidentDialogProps) {
  const { community } = useUser();
  const [serverError, setServerError] = React.useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ResidentFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      unit_id: '',
      community_role: 'community_member',
      unit_role: 'unit_resident',
      send_invite: true,
    },
  });

  const communityRole = watch('community_role');
  const unitNotRequired = ROLES_WITHOUT_UNIT_REQUIREMENT.includes(communityRole ?? '');

  // Fetch units for the dropdown
  const { data: unitsData, isLoading: unitsLoading } = useQuery({
    queryKey: ['units', communityId],
    queryFn: async () => await getAllUnits(),
    enabled: Boolean(community?.id) && open,
  });

  const unitsList = React.useMemo(() => {
    if (!unitsData) return [] as any[];
    if (Array.isArray((unitsData as any).units)) return (unitsData as any).units as any[];
    if (Array.isArray(unitsData)) return unitsData as any[];
    return [] as any[];
  }, [unitsData]);

  // A community member must live in a unit. Surface this clearly when the
  // selected role requires a unit but the community has none yet.
  const requiresUnit = !unitNotRequired;
  const hasNoUnits = requiresUnit && !unitsLoading && unitsList.length === 0;

  const createResidentMutation = useMutation({
    mutationFn: async (data: ResidentFormData) => {
      if (!unitNotRequired && !data.unit_id) {
        throw new Error('Unit is required for community members.');
      }
      return await createResident(community?.id, data);
    },
    onSuccess: (response: any) => {
      setServerError(null);
      reset();
      if (response?.existing_user) {
        toast.success('Existing user added to community successfully.');
      } else {
        toast.success('User created successfully.');
      }
      onCreated();
    },
    onError: (err: any) => {
      const errorCode = err?.response?.data?.error_code;
      const message = err?.response?.data?.message ?? err?.message ?? 'Something went wrong.';

      if (errorCode === 'already_in_community') {
        setServerError('This user is already a member of this community.');
      } else {
        setServerError(message);
      }
    },
  });

  const handleClose = () => {
    reset();
    setServerError(null);
    onClose();
  };

  const onSubmit = (data: ResidentFormData) => {
    setServerError(null);
    createResidentMutation.mutate(data);
  };

  return (
    <ProfessionalDialog
      actions={
        <DialogActionButtons
          confirmText={createResidentMutation.isPending ? 'Saving...' : 'Add User'}
          disabled={hasNoUnits}
          loading={createResidentMutation.isPending}
          onCancel={handleClose}
          onConfirm={handleSubmit(onSubmit)}
        />
      }
      contentSx={{ pt: 2, px: 3, pb: 2 }}
      icon={<UserPlusIcon size={20} />}
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      subtitle="Add a new or existing user and optionally assign a unit"
      title="Add User"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError(null)}>
            {serverError}
          </Alert>
        )}
        {hasNoUnits && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            A community member must be assigned to a unit, but this community doesn&apos;t have any units yet.{' '}
            <Link component={RouterLink} to={paths.units.index} fontWeight={600}>
              Create a unit first
            </Link>
            , then come back to add the member. (Board members and managers can be added without a unit.)
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="first_name"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.first_name)}
                  fullWidth
                  helperText={errors.first_name?.message}
                  label="First Name"
                  placeholder="First Name"
                />
              )}
              rules={{ required: 'First name is required' }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="last_name"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.last_name)}
                  fullWidth
                  helperText={errors.last_name?.message}
                  label="Last Name"
                  placeholder="Last Name"
                />
              )}
              rules={{ required: 'Last name is required' }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.email)}
                  fullWidth
                  helperText={errors.email?.message}
                  label="Email Address"
                  placeholder="Email Address"
                />
              )}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.phone)}
                  fullWidth
                  helperText={errors.phone?.message}
                  label="Phone Number"
                  placeholder="(123) 456-7890"
                />
              )}
            />
          </Grid>
          {/* Community Role Selection */}
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="community_role"
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Community Role</InputLabel>
                  <Select {...field} label="Community Role">
                    <MenuItem value="community_member">Community Member</MenuItem>
                    <MenuItem value="board_member">Board Member</MenuItem>
                    <MenuItem value="community_manager">Community Manager</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          {/* Unit Assignment and Role */}
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="unit_id"
              render={({ field }) => (
                <FormControl error={Boolean(errors.unit_id)} fullWidth>
                  <InputLabel>Assign to Unit</InputLabel>
                  <Select {...field} disabled={unitNotRequired} label="Assign to Unit">
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {unitsList.map((unit: any) => (
                      <MenuItem key={unit.id} value={unit.id.toString()}>
                        {unit.address}
                        {unit.unit_number ? ` #${unit.unit_number}` : ''}
                        {unit.current_residents_count ? ` (${unit.current_residents_count})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.unit_id?.message ?? (requiresUnit ? 'Required for community members' : 'Optional for this role')}
                  </FormHelperText>
                </FormControl>
              )}
              rules={{
                validate: (value) =>
                  unitNotRequired || Boolean(value) || 'A community member must be assigned to a unit.',
              }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="unit_role"
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Unit Role</InputLabel>
                  <Select {...field} disabled={!watch('unit_id')} label="Unit Role">
                    <MenuItem value="unit_owner">Unit Owner</MenuItem>
                    <MenuItem value="unit_tenant">Unit Tenant</MenuItem>
                    <MenuItem value="unit_resident">Unit Resident</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          {/* Invite Toggle */}
          <Grid item xs={12}>
            <Controller
              control={control}
              name="send_invite"
              render={({ field }) => (
                <FormControlLabel control={<Checkbox {...field} checked={Boolean(field.value)} />} label="Send welcome email" />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </ProfessionalDialog>
  );
}
